/**
 * Odino agent API client (ADR-0024). Settings (which LLM config Odino uses) + the
 * session → turn → SSE-stream lifecycle. The stream uses `EventSource`, which can't
 * set headers, so the JWT rides as `?token=` (the backend `current_user` accepts it),
 * mirroring `eventStream.tsx`.
 */
import { api } from "./api";

/** One selectable LLM configuration. */
export interface OdinoConfigOption {
  id: string;
  name: string;
  provider: string;
  model: string;
  is_default: boolean;
}

export interface OdinoSettings {
  /** The configs Odino can be bound to — authored in Configurazioni LLM, not here. */
  configs: OdinoConfigOption[];
  /** Odino's OWN binding; null ⇒ inherit the instance default. */
  config_id: string | null;
  /** The RESOLVED config's identity (Odino's binding, else the default) — display only. */
  provider: string | null;
  model: string | null;
  base_url: string | null;
  configured: boolean;
}

/** Bind Odino to a config. `null` ⇒ inherit the default.
 *
 * It used to carry provider/model/api_key/base_url and author the instance DEFAULT
 * config from this page (ADR-0071 retired that — a default wearing Odino's name).
 * Configs live at `/ai/llm-configs`. */
export interface OdinoSettingsIn {
  config_id: string | null;
}

// --- Voice STT/TTS provider settings (ADR-0025) ---------------------------

export interface VoiceProviderInfo {
  provider_id: string;
  label: string;
  description: string;
  needs_key: boolean;
  voices: string[];
  models: string[];
}

export interface VoiceStageSettings {
  providers: VoiceProviderInfo[];
  provider: string | null;
  model: string | null;
  voice: string | null;
  configured: boolean;
}

export interface VoiceSettings {
  stt: VoiceStageSettings;
  tts: VoiceStageSettings;
}

export interface VoiceStageIn {
  provider: string;
  model?: string;
  voice?: string;
  /** Supply to set/rotate the key; omit to keep the stored one. */
  api_key?: string;
}

/** A persisted conversation in the caller's OWN history (per-user). */
export interface OdinoSessionSummary {
  id: string;
  title: string | null;
  status: string;
  message_count: number;
  created_at: string | null;
  updated_at: string | null;
}

/** One persisted block, as stored (thinking is never persisted — see backend
 *  serialize.py). The frontend converts these to display turns on reopen. */
export type OdinoStoredBlock =
  | { t: "text"; text: string }
  | { t: "tool_use"; id: string; name: string; input: Record<string, unknown> }
  | { t: "tool_result"; tool_use_id: string; content: string; is_error: boolean };

export interface OdinoStoredMessage {
  role: "user" | "assistant";
  content: OdinoStoredBlock[];
  seq: number;
  created_at: string | null;
}

// Paths are RELATIVE to the `api` wrapper's BASE ("/api/admin") — passing the
// full "/api/admin/..." here would double-prefix into
// "/api/admin/api/admin/..." → 404. Only `streamUrl` is absolute, because the
// EventSource hits it directly (it does NOT go through the `api` wrapper).
export const odinoApi = {
  getSettings: () => api.get<OdinoSettings>("/odino/settings"),
  putSettings: (body: OdinoSettingsIn) => api.put<OdinoSettings>("/odino/settings", body),
  // Voice STT/TTS provider settings (ADR-0025) — same page, separate config.
  getVoiceSettings: () => api.get<VoiceSettings>("/odino/voice-settings"),
  putVoiceStt: (body: VoiceStageIn) => api.put<VoiceSettings>("/odino/voice-settings/stt", body),
  putVoiceTts: (body: VoiceStageIn) => api.put<VoiceSettings>("/odino/voice-settings/tts", body),
  createSession: () => api.post<{ id: string }>("/odino/sessions", {}),
  /** The caller's OWN conversation history (per-user; most-recent first). */
  listSessions: () => api.get<OdinoSessionSummary[]>("/odino/sessions"),
  /** The persisted transcript of one of the caller's sessions (for reopen). */
  getSessionMessages: (sessionId: string) =>
    api.get<OdinoStoredMessage[]>(`/odino/sessions/${sessionId}/messages`),
  /** Delete one of the caller's sessions (transcript cascades). */
  deleteSession: (sessionId: string) => api.delete<{ ok: boolean }>(`/odino/sessions/${sessionId}`),
  postTurn: (sessionId: string, message: string, context?: object | null, thinking?: boolean) =>
    api.post<{ ok: boolean }>(`/odino/sessions/${sessionId}/turn`, { message, context, thinking }),
  cancel: (sessionId: string) =>
    api.post<{ ok: boolean }>(`/odino/sessions/${sessionId}/cancel`, {}),
  /** SSE endpoint for a session's turn progress (token in the query). Absolute:
   *  the EventSource fetches it directly, bypassing the `api` wrapper/BASE. */
  streamUrl: (sessionId: string, token: string) =>
    `/api/admin/odino/sessions/${sessionId}/stream?token=${encodeURIComponent(token)}`,
};
